import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import type {ColumnDef} from "@tanstack/react-table";
import type {Page} from "crqlar-react-common/dist/models/common/page.model";
import type {SyncTask} from "crqlar-react-common/dist/models/integrations/syncTask.model";
import type {TasksListRequest} from "crqlar-react-common/dist/models/integrations/tasksListRequest.dto";
import {formatDateDMYHM, formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import type {ReactNode} from "react";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    ServerTableToolbarAction,
    useServerTable
} from "../../../common/serverTable";
import SyncTaskPreviewDialog from "../../common/SyncTaskPreviewDialog";

export interface SyncTaskTableAction {
    id: string
    label: ReactNode
    startIcon?: ReactNode
    onClick: () => void | Promise<unknown>
    refreshAfter?: boolean
    disabled?: boolean
}

interface Props {
    configId?: string
    tableId: string
    list: (request: TasksListRequest) => Promise<Page<SyncTask>>
    actions?: SyncTaskTableAction[]
    renderDialogs?: (refresh: () => void) => ReactNode
}

const SyncTasksTable = ({configId, tableId, list, actions = [], renderDialogs}: Props) => {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const [selectedTask, setSelectedTask] = useState<SyncTask>();
    const [runningActionId, setRunningActionId] = useState<string>();
    const [actionError, setActionError] = useState(false);

    const columns = useMemo<ColumnDef<SyncTask>[]>(() => [
        {
            id: 'type',
            header: t('Type'),
            accessorFn: task => task.syncType,
            cell: ({row}) => <Box>{row.original.syncType || '-'}</Box>,
            size: 220,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'syncType',
                grow: true
            }
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: task => task.status,
            cell: ({row}) => <Box>{row.original.status || '-'}</Box>,
            size: 160,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'processingStart',
            header: t('Processing start'),
            accessorFn: task => task.processingStart,
            cell: ({row}) => <Box>{formatDateDMYHM(row.original.processingStart) || '-'}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'processingDone',
            header: t('Processing done'),
            accessorFn: task => task.processingEnd,
            cell: ({row}) => <Box>{formatDateDMYHM(row.original.processingEnd) || '-'}</Box>,
            size: 200,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'processingEnd'
            }
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: task => task.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 300,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: task => task.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId,
        columns,
        getRowId: task => task.id!,
        enabled: !!configId,
        queryDependencies: [configId],
        initialSorting: [{id: 'processingStart', desc: true}],
        onRowClick: setSelectedTask,
        queryFn: async ({pagination, sorting}) => {
            const page = await list({
                configIds: [configId!],
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    const refresh = () => {
        void controller.query.refetch();
    };
    const runAction = async (action: SyncTaskTableAction) => {
        setActionError(false);
        setRunningActionId(action.id);
        try {
            await action.onClick();
            if (action.refreshAfter !== false) {
                await controller.query.refetch();
            }
        } catch (error) {
            console.error(error);
            setActionError(true);
        } finally {
            setRunningActionId(undefined);
        }
    };

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', height: '70vh', minHeight: 0}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <ServerTableToolbar>
                            <ServerTableSort/>
                            <ServerTableFullscreen/>

                            <ServerTableColumns/>

                            {actions.map(action => (
                                <ServerTableToolbarAction
                                    key={action.id}
                                    variant="contained"
                                    startIcon={action.startIcon}
                                    loading={runningActionId === action.id}
                                    disabled={!configId || action.disabled || !!runningActionId || readOnly}
                                    onClick={() => void runAction(action)}
                                >
                                    {action.label}
                                </ServerTableToolbarAction>
                            ))}
                        </ServerTableToolbar>

                        <ServerTable/>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            <SyncTaskPreviewDialog
                open={!!selectedTask}
                syncTaskId={selectedTask?.id}
                queryScope={tableId}
                getTask={async taskId => {
                    const page = await list({
                        id: [taskId],
                        configIds: [configId!],
                        limit: {offset: 0, limit: 1}
                    });
                    return page.content?.[0];
                }}
                onClose={() => setSelectedTask(undefined)}
            />

            {renderDialogs?.(refresh)}

            <Snackbar
                open={actionError}
                autoHideDuration={6000}
                onClose={() => setActionError(false)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert severity="error" onClose={() => setActionError(false)}>
                    {t('Something went wrong')}
                </Alert>
            </Snackbar>
        </>
    );
};

export default SyncTasksTable;
